import { Button, Row, Col, message, Divider, Tabs } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import api from '../../api'
import { useAsync } from '../../hooks'
import { useTransmissions } from '../../redux/slices/transmissions'
import { useCurrentUser } from '../../redux/slices/session'
import { ListError, PageHeader } from '../Common'
import { toPath } from '../../routes'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'
import PDFPreview from '../Common/PDFPreview'
import DownloadLink from '../Common/DownloadLink'
import PatientSummary from '../Common/PatientSummary'
import Loading from '../Loading'
import TransmissionSidebar from '../Common/TransmissionSidebar'
import TransmissionForm from './Form'

const { TabPane } = Tabs

export default function Show() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [transmission, setTransmission] = useState({})
  const [isArchived, setArchiveStatus] = useState()
  const [patientRecord, setPatientRecord] = useState(null)
  const { role } = useCurrentUser()
  const isClinician = role === 'clinician'

  useEffect(() => {
    const patientId = transmission?.patient?.id

    if (patientId)
      api.patients.get(patientId).then(record => {
        setPatientRecord(record)
      })
  }, [transmission])

  const getTransmission = useCallback(async () => {
    setTransmission(await api.transmissions.get(id))
    setArchiveStatus(transmission.isArchived)
  }, [id, transmission.isArchived])
  const transmissions = useTransmissions()

  const onSubmit = async ({ patientId, deviceModelId, useCurrentDevice }) => {
    let deviceId

    const deviceUpdateData = {
      deviceModelId,
      serialNumber: transmission.device.serialNumber,
    }

    try {
      if (!useCurrentDevice) {
        const patient = await api.patients.createDevice(
          patientId,
          deviceUpdateData,
        )

        const matchDevice = patient.devices.find(
          device => device.serialNumber === deviceUpdateData.serialNumber,
        )

        deviceId = matchDevice.id
      }

      const transmissionUpdateData = {
        patientId,
        deviceId,
        main: transmission.main,
      }

      setTransmission(
        await api.transmissions.update(id, transmissionUpdateData),
      )

      message.success('Transmission updated successfully')
    } catch (e) {
      message.error(
        `There was an error updating the transmission. ${e.message}`,
      )
      throw e
    }
  }

  const { error, pending } = useAsync(getTransmission)

  const dismissTransmission = useCallback(
    async nextId => {
      try {
        await api.transmissions.dismiss(id)
        message.success('The transmission has been dismissed')
        if (nextId) {
          navigate(toPath.transmissions.transmission({ id: nextId }), {
            replace: true,
          })
        } else {
          navigate(-1)
        }
      } catch (e) {
        message.error(
          `There was an error dismissing the transmission: ${e.message}`,
        )
      }
    },
    [id, navigate],
  )

  const showArchiveButtonState = () => {
    let button = null
    const currentIndex = transmissions.findIndex(tr => tr.id === id)
    const nextId =
      currentIndex < transmissions.length - 1
        ? transmissions[currentIndex + 1].id
        : null

    if (isArchived) {
      button = <Button disabled> Archived </Button>
    } else {
      button = (
        <Button onClick={() => dismissTransmission(nextId)}>
          Dismiss and Next
        </Button>
      )
    }

    return button
  }

  const setAlertInformation = () => {
    const alertInformation = {
      fileUrl: transmission.fileUrl,
      patientId: transmission.patient.id,
      practiceId: transmission.practice.id,
      transmissionId: transmission.id,
      returnTo: location.state?.returnTo,
    }

    sessionStorage.setItem('alertInformation', JSON.stringify(alertInformation))
    navigate(toPath.alerts.new())
  }

  if (pending) {
    return <Loading large />
  }

  if (error) {
    return (
      <ListError
        message="There was an error loading this transmission"
        description={error.message}
      />
    )
  }

  const { fileUrl } = transmission

  return (
    <div>
      <PageHeader
        title="Transmission"
        onBack={() => navigate(-1)}
        extra={
          <>
            {patientRecord && !isClinician && (
              <Link
                key="edit-patient"
                to={toPath.patients.patient.edit({ id: patientRecord.id })}
              >
                <Button>Edit Patient</Button>
              </Link>
            )}

            <Button onClick={() => setAlertInformation()}>Add new alert</Button>
            {showArchiveButtonState()}

            {fileUrl && (
              <DownloadLink path={fileUrl}>
                <Button icon={<DownloadOutlined />}>Download</Button>
              </DownloadLink>
            )}
          </>
        }
      />
      {patientRecord && <PatientSummary patient={patientRecord} />}
      <Divider />
      <div className="transmission-content">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Tabs defaultActiveKey="1" className="tabs">
              <TabPane tab="Transmission" key="transmission">
                <PDFPreview path={transmission.fileUrl} showAllPages />
              </TabPane>
            </Tabs>
          </Col>
          <Col xs={24} md={8}>
            {patientRecord ? (
              <TransmissionSidebar
                patient={patientRecord}
                practiceId={patientRecord.practice.id}
              />
            ) : (
              <TransmissionForm
                practice={transmission.practice}
                deviceSerialNumber={transmission.device.serialNumber}
                onSubmit={onSubmit}
              />
            )}
          </Col>
        </Row>
      </div>

      <style>{`
        .tabs {
          margin-right: 20px;
          max-height: 80vh;
          overflow-y: auto;
        }

        .transmission-content {
          padding-bottom: 0;
        }

        ${MOBILE_MEDIA_QUERY} {
          .transmission-content {
            padding-bottom: 20px;
          }

          .tabs {
            margin-right: 0;
            max-height: none;
          }
        }
      `}</style>
    </div>
  )
}
